import { formatPrice } from '../../util/price.js';

/**
 * TVA par pays de livraison.
 *
 * Principe : le prix TTC payé par le client ne bouge jamais (un livre à 39,99 EUR
 * reste à 39,99 EUR en France comme en Allemagne). Seule la répartition HT / TVA
 * change selon le pays. Un pays absent de la table est facturé à 0% (hors UE).
 *
 * Helpers volontairement purs : ils sont partagés par le panier, la commande et
 * le serveur (server/api/vat/vat.helper.js).
 */

/**
 * Taux applicable à un pays.
 *
 * @returns {number|null} fraction (0.055) ou null s'il ne faut rien changer :
 * pays inconnu du panier, ou table pas encore chargée côté client.
 */
export function getVatRateForCountry(vatRates, countryCode) {
	const code = `${countryCode || ''}`.trim().toUpperCase();
	if(!code || !(vatRates || []).length) {
		return null;
	}
	const vatRate = vatRates.find(rate => `${(rate || {}).countryCode || ''}`.trim().toUpperCase() === code);
	return vatRate ? Math.min(Math.max(parseFloat(vatRate.rate) || 0, 0), 1) : 0;
}

/**
 * Recalcule le HT d'une ligne au taux du pays sans jamais toucher au prix TTC.
 */
export function applyVatRateToItem(item, rate) {
	if(!item || rate === null || parseFloat(item.taxRate) === rate) {
		return item;
	}
	const basePriceTTC = parseFloat(item.basePriceTTC);
	return {
		...item,
		taxRate: `${rate}`,
		priceHT: formatPrice((parseFloat(item.priceTTC) || 0) / (1 + rate)),
		...(Number.isNaN(basePriceTTC) ? {} : { basePriceHT: formatPrice(basePriceTTC / (1 + rate)) }),
	};
}

export function applyVatRateToItems(items, rate) {
	if(rate === null || !(items || []).length) {
		return items;
	}
	return items.map(item => applyVatRateToItem(item, rate));
}

/**
 * Taux à appliquer aux frais de port : celui du produit principal (ligne la plus
 * chère). Après application du taux pays, toutes les lignes partagent ce taux.
 */
export function getItemsVatRate(items) {
	const orderedItems = (items || []).filter(item => item && (!item.status || item.status === 'ordered'));
	const mainItem = orderedItems.reduce((main, item) => (getItemTotalTTC(item) > getItemTotalTTC(main) ? item : main), null);
	return mainItem ? parseFloat(mainItem.taxRate) || 0 : 0;
}

function getItemTotalTTC(item) {
	if(!item) {
		return -1;
	}
	return (parseFloat(item.priceTTC) || 0) * (parseInt(item.quantity, 10) || 0);
}

/**
 * Déduit une remise (bon d'achat) d'un montant de TVA déjà calculé sur la base
 * avant remise.
 *
 * Les réductions de prix consenties au client sortent de la base imposable
 * (CGI article 267-II-1, directive 2006/112/CE article 79) : la TVA porte sur
 * ce que le client paie réellement.
 *
 * Répartir la remise au prorata des bases par taux revient exactement à
 * appliquer un même facteur à chaque taux, d'où ce simple ratio. Il reste donc
 * juste même sur une ancienne commande qui mélange plusieurs taux.
 *
 * @param vat TVA calculée sur baseTTC
 * @param baseTTC total TTC avant remise (articles + frais de port)
 * @param discount remise TTC
 */
export function applyDiscountToVat(vat, baseTTC, discount) {
    const vatAmount = parseFloat(vat) || 0;
    const base = parseFloat(baseTTC) || 0;
    if(!vatAmount || base <= 0) {
        return formatPrice(vatAmount);
    }
    const discountAmount = Math.min(Math.max(parseFloat(discount) || 0, 0), base);
    return formatPrice(vatAmount * (1 - (discountAmount / base)));
}

/**
 * Part de TVA contenue dans un montant TTC (frais de port notamment).
 */
export function getVatFromTTC(amountTTC, rate) {
	const amount = parseFloat(amountTTC) || 0;
	const vatRate = parseFloat(rate) || 0;
	if(!amount || !vatRate) {
		return 0;
	}
	return formatPrice(amount - (amount / (1 + vatRate)));
}
